import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  List,
  ListItem,
  LinearProgress,
  Alert,
} from '@mui/material';
import apiService from '../../services/api';
import { createLogger, adminLogContext } from '../../utils/logger'

const logger = createLogger('CountyAccessModal')
const ctx = (extra) => adminLogContext('user_management', extra)


const CountyAccessModal = ({ open, onClose, user }) => {
  const [allCounties, setAllCounties] = useState([]);
  const [selectedCountyIds, setSelectedCountyIds] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !user?.id) return;

    if (user.is_admin) {
      setLoading(false);
      setError('');
      setAllCounties([]);
      setSelectedCountyIds(new Set());
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // 1) Fetch all counties in the system (admin endpoint with stats)
        const countyStats = await apiService.getCountyDocumentStatus();
        const normalizedCounties = (countyStats || []).map((c) => ({
          id: c.county_id,
          name: c.name,
          fullName: c.full_name || `${c.name}, ${c.state}`,
        }));
        // Sort for a stable, readable list
        normalizedCounties.sort((a, b) => a.fullName.localeCompare(b.fullName));
        setAllCounties(normalizedCounties);

        // 2) Fetch this user's county access
        const userCounties = await apiService.getUserCountyAccess(user.id);
        const countyIdSet = new Set((userCounties || []).map((c) => c.id));
        setSelectedCountyIds(countyIdSet);
      } catch (err) {
        logger.apiFailure('Failed to load user county access', ctx({
          action: 'load_user_county_access',
          user_id: String(user.id),
        }), err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, user]);

  const handleToggleCounty = (countyId) => {
    const next = new Set(selectedCountyIds);
    if (next.has(countyId)) {
      next.delete(countyId);
    } else {
      next.add(countyId);
    }
    setSelectedCountyIds(next);
  };

  const handleSaveChanges = async () => {
    try {
      setLoading(true);
      await apiService.updateUserCountyAccess(user.id, Array.from(selectedCountyIds));
      onClose(); // Close modal on success
    } catch (err) {
      logger.apiFailure('Failed to save user county access', ctx({
        action: 'save_user_county_access',
        user_id: String(user.id),
        county_count: selectedCountyIds.size,
      }), err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ zIndex: 1700 }}
    >
      <DialogTitle>Manage County Access for {user?.email}</DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 1 }} />}
        {user?.is_admin ? (
          <Alert severity="info">
            Admin accounts always have access to <strong>all counties</strong>. Access is not stored in
            per-county assignments, so it stays correct when new counties are added. No changes are needed here.
          </Alert>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <FormGroup>
            <List sx={{ maxHeight: 400, overflow: 'auto', opacity: loading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {allCounties.map((county) => (
                <ListItem key={county.id} dense>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedCountyIds.has(county.id)}
                        onChange={() => handleToggleCounty(county.id)}
                      />
                    }
                    label={county.fullName}
                  />
                </ListItem>
              ))}
            </List>
          </FormGroup>
        )}
      </DialogContent>
      <DialogActions>
        {user?.is_admin ? (
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={handleSaveChanges} variant="contained" disabled={loading}>
              Save Changes
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CountyAccessModal;